import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Twitter, ArrowRight, MessageCircle, Facebook, Mail, Globe, Settings, Plus } from 'lucide-react';
import { initialCMSContent } from '../data/cms';
import TikTokIcon from './icons/TikTokIcon';
import ShopeeIcon from './icons/ShopeeIcon';
import { useStorefrontData } from '../context/StorefrontDataContext';
import EditableText from './live-editor/EditableText';

export default function Footer() {
  const { cms: storefrontCms, settings, isAdmin, isEditMode, isPreviewMode, updateCmsField, openMasterDrawer } = useStorefrontData();
  const cms = storefrontCms || initialCMSContent;

  const canEdit = Boolean(isAdmin && isEditMode && !isPreviewMode);
  const groupedLinks = Array.isArray(cms.footer?.links) ? cms.footer.links : [];

  const rawPhone = settings?.whatsapp_number || cms.footer?.phone || '';
  let cleanPhone = rawPhone.replace(/[^0-9]/g, '');
  if (cleanPhone.startsWith('0')) {
    cleanPhone = '62' + cleanPhone.slice(1);
  }
  const whatsappUrl = `https://wa.me/${cleanPhone}`;

  const campaignSubtitle = cms.footer?.campaignSubtitle || "Dapatkan komisi eksklusif dengan berbagi keanggunan";
  const campaignTitle = cms.footer?.campaignTitle || "BERGABUNG SEBAGAI AFFILIATOR SECERA";
  const campaignCtaText = cms.footer?.campaignCtaText || "HUBUNGI WHATSAPP";
  const campaignCtaLink = cms.footer?.campaignCtaLink || whatsappUrl;

  const instagramLink = cms.footer?.instagramUrl || settings?.instagram_url;
  const facebookLink = cms.footer?.facebookUrl || settings?.facebook_url;
  const twitterLink = cms.footer?.twitterUrl || settings?.twitter_url;
  const tiktokLink = cms.footer?.tiktokUrl || settings?.tiktok_url;
  const footerWhatsappUrl = cms.footer?.whatsappUrl || whatsappUrl;
  const shopeeLink = cms.footer?.shopeeUrl || '';

  return (
    <footer className="bg-[#722F38] text-[#F9F9F9] border-t border-[#F9F9F9]/12 relative">
      {/* Master Component Bar for Admin Edit Mode */}
      {canEdit && (
        <div className="bg-amber-950/90 border-b border-amber-500/30 px-6 md:px-12 py-2.5 text-xs text-amber-200 flex flex-wrap items-center justify-between gap-3 shadow-inner">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="font-mono text-[10px] tracking-wider uppercase font-bold text-amber-300 flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" />
              Master Component: Footer Sitewide
            </span>
            <span className="text-amber-200/70 text-[11px] hidden sm:inline">
              (Semua perubahan link, sosmed, dan teks berlaku di seluruh halaman)
            </span>
          </div>

          <button
            type="button"
            onClick={() => openMasterDrawer('footer')}
            className="px-3 py-1 bg-amber-400/20 hover:bg-amber-400/30 text-amber-200 hover:text-white rounded-lg border border-amber-400/40 font-semibold text-[11px] flex items-center gap-1.5 transition-all cursor-pointer active:scale-95"
          >
            <Settings className="w-3.5 h-3.5 text-amber-300" />
            <span>Kelola Kolom & Sosmed Master ↗</span>
          </button>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.4fr] gap-10 lg:gap-16">
          {/* Left Column: Campaign & Socials */}
          <div>
            {canEdit ? (
              <div className="mb-2">
                <EditableText
                  value={campaignSubtitle}
                  onChange={(val) => updateCmsField('footer', 'campaignSubtitle', val)}
                  as="p"
                  label="Subjudul Kampanye Footer"
                  className="text-label text-[#F9F9F9]/80 mb-2 cursor-pointer hover:bg-white/10 px-1 py-0.5 rounded"
                />
              </div>
            ) : (
              <p className="text-label text-[#F9F9F9]/80 mb-2">{campaignSubtitle}</p>
            )}

            {canEdit ? (
              <div className="mb-6">
                <EditableText
                  value={campaignTitle}
                  onChange={(val) => updateCmsField('footer', 'campaignTitle', val)}
                  as="h3"
                  label="Judul Utama Kampanye Footer"
                  className="font-serif text-2xl md:text-3xl text-white max-w-md leading-tight cursor-pointer hover:bg-white/10 px-1 py-0.5 rounded"
                />
              </div>
            ) : (
              <h3 className="font-serif text-2xl md:text-3xl text-white mb-6 max-w-md leading-tight">{campaignTitle}</h3>
            )}

            <div className="flex mb-8">
              {canEdit ? (
                <div className="flex items-center gap-2">
                  <a 
                    href={campaignCtaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.preventDefault()}
                    className="h-12 px-8 bg-[#F9F9F9] text-[#5A252D] text-label hover:bg-white transition-all inline-flex items-center justify-center gap-3 group"
                  >
                    {campaignCtaText}
                    <MessageCircle className="w-4 h-4 transition-transform group-hover:scale-110" />
                  </a>
                  <button
                    type="button"
                    onClick={() => openMasterDrawer('footer')}
                    className="p-2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg text-xs"
                    title="Ubah teks & link tombol"
                  >
                    Edit Tombol
                  </button>
                </div>
              ) : (
                <a 
                  href={campaignCtaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-12 px-8 bg-[#F9F9F9] text-[#5A252D] text-label hover:bg-white transition-all inline-flex items-center justify-center gap-3 group"
                >
                  {campaignCtaText}
                  <MessageCircle className="w-4 h-4 transition-transform group-hover:scale-110" />
                </a>
              )}
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-2 text-[#F9F9F9]/85">
              {instagramLink && (
                <a href={instagramLink} target="_blank" rel="noopener noreferrer" className="inline-flex w-9 h-9 items-center justify-center hover:text-white transition-colors" aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {facebookLink && (
                <a href={facebookLink} target="_blank" rel="noopener noreferrer" className="inline-flex w-9 h-9 items-center justify-center hover:text-white transition-colors" aria-label="Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {twitterLink && (
                <a href={twitterLink} target="_blank" rel="noopener noreferrer" className="inline-flex w-9 h-9 items-center justify-center hover:text-white transition-colors" aria-label="Twitter">
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {tiktokLink && (
                <a href={tiktokLink} target="_blank" rel="noopener noreferrer" className="inline-flex w-9 h-9 items-center justify-center hover:text-white transition-colors" aria-label="TikTok">
                  <TikTokIcon className="w-5 h-5" />
                </a>
              )}
              {shopeeLink && (
                <a href={shopeeLink} target="_blank" rel="noopener noreferrer" className="inline-flex w-9 h-9 items-center justify-center hover:text-white transition-colors" aria-label="Shopee">
                  <ShopeeIcon className="w-5 h-5" />
                </a>
              )}
              {footerWhatsappUrl && (
                <a href={footerWhatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex w-9 h-9 items-center justify-center hover:text-white transition-colors" aria-label="WhatsApp">
                  <MessageCircle className="w-5 h-5" />
                </a>
              )}

              {canEdit && (
                <button
                  type="button"
                  onClick={() => openMasterDrawer('footer')}
                  className="ml-2 px-2 py-1 text-[10px] font-mono bg-white/10 hover:bg-white/20 text-white/90 rounded border border-white/20 cursor-pointer"
                  title="Kelola link sosmed di drawer"
                >
                  + Sosmed
                </button>
              )}
            </div>
          </div>

          {/* Right Column: Dynamic Link Columns & Company Details */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-8">
            {groupedLinks.map((group: any, idx: number) => (
              <div key={group.id || idx} className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-label text-[#F9F9F9]/80 font-bold">{group.title}</p>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => openMasterDrawer('footer')}
                      className="text-[9px] text-amber-300 hover:underline opacity-80 hover:opacity-100"
                      title="Edit kolom ini di drawer"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {group.items?.map((item: any, itemIdx: number) => (
                  <a
                    key={item.id || itemIdx}
                    href={item.url}
                    className="block text-sm text-[#F9F9F9]/88 hover:text-white transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            ))}

            {/* Quick add column trigger if editing */}
            {canEdit && (
              <button
                type="button"
                onClick={() => openMasterDrawer('footer')}
                className="rounded-xl border border-dashed border-white/25 hover:border-amber-300/60 hover:bg-white/5 p-4 flex flex-col items-center justify-center text-center gap-2 text-xs text-white/70 hover:text-white transition-all cursor-pointer min-h-[100px]"
              >
                <Plus className="w-4 h-4 text-amber-300" />
                <span className="font-semibold">+ Kelola Kolom</span>
              </button>
            )}

            {/* Company Info Box */}
            <div className="space-y-3">
              <p className="text-label text-[#F9F9F9]/80">Perusahaan</p>
              <div className="space-y-2">
                <p className="text-sm font-bold text-white">{settings?.company_name || 'SECERA'}</p>
                <p className="text-xs text-[#F9F9F9]/80 whitespace-pre-wrap">
                  {settings?.company_address || 'Indonesia'}
                </p>
                <div className="pt-2 space-y-2">
                  <p className="text-[10px] text-[#F9F9F9]/75 uppercase tracking-widest font-medium">Kontak</p>
                  <div className="space-y-1.5">
                    <a 
                      href={`mailto:${cms.footer?.email || 'care@secera.id'}`}
                      className="flex items-center gap-2 text-xs text-[#F9F9F9]/80 hover:text-white transition-colors group w-fit"
                    >
                      <Mail className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                      <span className="group-hover:underline decoration-[#F9F9F9]/30 underline-offset-2">{cms.footer?.email || 'care@secera.id'}</span>
                    </a>
                    <a 
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-[#F9F9F9]/80 hover:text-white transition-colors group w-fit"
                    >
                      <MessageCircle className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                      <span className="group-hover:underline decoration-[#F9F9F9]/30 underline-offset-2">{cms.footer?.phone || 'WhatsApp'}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Sub-Footer: Tagline, Copyright & Privacy Policy */}
      <div className="border-t border-[#F9F9F9]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-[#F9F9F9]/80">
          <p>
            {cms.footer?.copyright || '©Copyright 2026 Secera'}
            {cms.footer?.tagline ? ` — ${cms.footer?.tagline}` : ''}
          </p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-white transition-colors">Syarat & Ketentuan</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Kebijakan Privasi</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
